import omit from 'lodash/omit';
import { attendeeUsageAPI } from '../../api/clients/attendeeUsageAPI';
import { episodeRsvpAPI } from '../../api/clients/episodeRsvpAPI';
import { types as sdkTypes } from '../../util/sdkLoader';
import { resetToStartOfDay, getDefaultTimeZoneOnBrowser } from '../../util/dates';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { availabilityExceptionsAPI, listingsAPI } from '../../api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { payoutsEnabled } from '../../ducks/stripe.duck';
import { googleStorageAPI } from '../../util/api';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import config from '../../config';
import * as log from '../../util/log';
import {
  editLiveRoomIncludeParams,
  editLiveRoomImageUploadParams,
  imageVariantsForSets,
} from '../../util/imageVariants';
import { prepareImageForUpload } from '../../util/resizeImage';

const { UUID } = sdkTypes;

// Helper to get the correct public data property based on imageType
const getPromotePublicDataProperty = imageType => {
  return imageType === 'poster' ? 'posterImageId' : 'coverImageId';
};

// Helper to get the correct public data property based on videoType
const getPromoteVideoPublicDataProperty = videoType => {
  return videoType === 'cover' ? 'coverVideoId' : 'coverVideoId';
};

// Helper to select the correct SDK based on ownership
const getListingSdk = (isOwnListing, sdk) => {
  return isOwnListing ? sdk.ownListings : listingsAPI;
};

// Return an array of image ids
const imageIds = images => {
  // For newly uploaded image the UUID can be found from "img.imageId"
  // and for existing listing images the id is "img.id"
  return images ? images.map(img => img.imageId || img.id) : null;
};

// After listing creation & update, we want to make sure that uploadedImages state is cleaned
const updateUploadedImagesState = (state, payload) => {
  const { uploadedImages, uploadedImagesOrder } = state;

  // Images attached to listing entity
  const attachedImages = payload?.data?.relationships?.images?.data || [];
  const attachedImageUUIDStrings = attachedImages.map(img => img.id.uuid);

  // Uploaded images (which are propably not yet attached to listing)
  const unattachedImages = Object.values(state.uploadedImages);
  const duplicateImageEntities = unattachedImages.filter(unattachedImg =>
    attachedImageUUIDStrings.includes(unattachedImg.imageId?.uuid)
  );

  return duplicateImageEntities.length > 0
    ? {
        uploadedImages: {},
        uploadedImagesOrder: [],
      }
    : {
        uploadedImages,
        uploadedImagesOrder,
      };
};

const requestAction = actionType => params => ({ type: actionType, payload: { params } });
const successAction = actionType => result => ({ type: actionType, payload: result.data });
const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

// ================ Action types ================ //

export const MARK_TAB_UPDATED = 'app/EditLiveRoomPage/MARK_TAB_UPDATED';
export const CLEAR_UPDATED_TAB = 'app/EditLiveRoomPage/CLEAR_UPDATED_TAB';
export const CLEAR_UPDATED_CONTENT = 'app/EditLiveRoomPage/CLEAR_UPDATED_CONTENT';

export const CREATE_LISTING_DRAFT_REQUEST = 'app/EditLiveRoomPage/CREATE_LISTING_DRAFT_REQUEST';
export const CREATE_LISTING_DRAFT_SUCCESS = 'app/EditLiveRoomPage/CREATE_LISTING_DRAFT_SUCCESS';
export const CREATE_LISTING_DRAFT_ERROR = 'app/EditLiveRoomPage/CREATE_LISTING_DRAFT_ERROR';

export const PUBLISH_LISTING_REQUEST = 'app/EditLiveRoomPage/PUBLISH_LISTING_REQUEST';
export const PUBLISH_LISTING_SUCCESS = 'app/EditLiveRoomPage/PUBLISH_LISTING_SUCCESS';
export const PUBLISH_LISTING_ERROR = 'app/EditLiveRoomPage/PUBLISH_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditLiveRoomPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditLiveRoomPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditLiveRoomPage/UPDATE_LISTING_ERROR';

export const SHOW_LISTINGS_REQUEST = 'app/EditLiveRoomPage/SHOW_LISTINGS_REQUEST';
export const SHOW_LISTINGS_SUCCESS = 'app/EditLiveRoomPage/SHOW_LISTINGS_SUCCESS';
export const SHOW_LISTINGS_ERROR = 'app/EditLiveRoomPage/SHOW_LISTINGS_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditLiveRoomPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditLiveRoomPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditLiveRoomPage/UPLOAD_IMAGE_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditLiveRoomPage/REMOVE_LISTING_IMAGE';

export const UPLOAD_VIDEO_REQUEST = 'app/EditLiveRoomPage/UPLOAD_VIDEO_REQUEST';
export const UPLOAD_VIDEO_SUCCESS = 'app/EditLiveRoomPage/UPLOAD_VIDEO_SUCCESS';
export const UPLOAD_VIDEO_ERROR = 'app/EditLiveRoomPage/UPLOAD_VIDEO_ERROR';

export const REMOVE_LISTING_VIDEO = 'app/EditLiveRoomPage/REMOVE_LISTING_VIDEO';

export const FETCH_EXCEPTIONS_REQUEST =
  'app/EditLiveRoomPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS =
  'app/EditLiveRoomPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/EditLiveRoomPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';

export const ADD_EXCEPTION_REQUEST = 'app/EditLiveRoomPage/ADD_AVAILABILITY_EXCEPTION_REQUEST';
export const ADD_EXCEPTION_SUCCESS = 'app/EditLiveRoomPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS';
export const ADD_EXCEPTION_ERROR = 'app/EditLiveRoomPage/ADD_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST =
  'app/EditLiveRoomPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS =
  'app/EditLiveRoomPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/EditLiveRoomPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const ADD_REPEATING_EXCEPTIONS_REQUEST =
  'app/EditLiveRoomPage/ADD_AVAILABILITY_EXCEPTIONS_REQUEST';
export const ADD_REPEATING_EXCEPTIONS_SUCCESS =
  'app/EditLiveRoomPage/ADD_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const ADD_REPEATING_EXCEPTIONS_ERROR =
  'app/EditLiveRoomPage/ADD_AVAILABILITY_EXCEPTIONS_ERROR';

export const DELETE_REPEATING_EXCEPTIONS_REQUEST =
  'app/EditLiveRoomPage/DELETE_AVAILABILITY_EXCEPTIONS_REQUEST';
export const DELETE_REPEATING_EXCEPTIONS_SUCCESS =
  'app/EditLiveRoomPage/DELETE_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const DELETE_REPEATING_EXCEPTIONS_ERROR =
  'app/EditLiveRoomPage/DELETE_AVAILABILITY_EXCEPTIONS_ERROR';

export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/EditLiveRoomPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/EditLiveRoomPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/EditLiveRoomPage/SAVE_PAYOUT_DETAILS_ERROR';

export const PROMOTE_IMAGE_REQUEST = 'app/EditLiveRoomPage/PROMOTE_IMAGE_REQUEST';
export const PROMOTE_IMAGE_SUCCESS = 'app/EditLiveRoomPage/PROMOTE_IMAGE_SUCCESS';
export const PROMOTE_IMAGE_ERROR = 'app/EditLiveRoomPage/PROMOTE_IMAGE_ERROR';

export const PROMOTE_VIDEO_REQUEST = 'app/EditLiveRoomPage/PROMOTE_VIDEO_REQUEST';
export const PROMOTE_VIDEO_SUCCESS = 'app/EditLiveRoomPage/PROMOTE_VIDEO_SUCCESS';
export const PROMOTE_VIDEO_ERROR = 'app/EditLiveRoomPage/PROMOTE_VIDEO_ERROR';

/**
 * Helper function that denormalises the availabilityExceptions from
 * integration API response (e.g we need to format date).
 *
 * @param {array} availabilityExceptions
 *
 * @return {array} denormalised availabilityExceptions array
 */
const denormaliseIntegrationAvailabilityExceptions = availabilityExceptions =>
  availabilityExceptions.map(e => ({
    ...e,
    attributes: {
      ...e.attributes,
      start: new Date(e.attributes.start),
      end: new Date(e.attributes.end),
    },
  }));

// ================ Reducer ================ //

const initialState = {
  // Free creators' attendee-hours this month: { limited, usedMinutes, limitMinutes, notice, reached }
  attendeeUsage: null,
  // Error instance placeholders for each endpoint
  createListingDraftError: null,
  publishingListing: null,
  publishListingError: null,
  updateListingError: null,
  uploadImageError: null,
  uploadVideoError: null,
  createListingDraftInProgress: false,
  submittedListingId: null,
  redirectToListing: false,
  uploadedImages: {},
  uploadedImagesOrder: [],
  removedImageIds: [],
  uploadedVideos: {},
  uploadedVideosOrder: [],
  removedVideoIds: [],
  fetchExceptionsError: null,
  fetchExceptionsInProgress: false,
  availabilityExceptions: [],
  addExceptionError: null,
  addExceptionInProgress: false,
  deleteExceptionId: null,
  deleteExceptionError: null,
  deleteExceptionInProgress: false,
  addRepeatingExceptionsInProgress: false,
  addRepeatingExceptionsError: null,
  deleteRepeatingExceptionsInProgress: false,
  deleteRepeatingExceptionsError: null,
  listingDraft: null,
  updatedTab: null,
  updateInProgress: false,
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  promoteImageInProgress: false,
  promoteImageError: null,
  promoteVideoInProgress: false,
  promoteVideoError: null,
};

export const SET_ATTENDEE_USAGE = 'app/EditLiveRoomPage/SET_ATTENDEE_USAGE';

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_ATTENDEE_USAGE:
      return { ...state, attendeeUsage: payload };
    case MARK_TAB_UPDATED:
      return { ...state, updatedTab: payload };
    case CLEAR_UPDATED_TAB:
      return { ...state, updatedTab: null, updateListingError: null };
    case CLEAR_UPDATED_CONTENT:
      return {
        ...state,
        uploadedImages: {},
        uploadedImagesOrder: [],
        removedImageIds: [],
        uploadedVideos: {},
        uploadedVideosOrder: [],
        removedVideoIds: [],
      };

    case CREATE_LISTING_DRAFT_REQUEST:
      return {
        ...state,
        createListingDraftInProgress: true,
        createListingDraftError: null,
        submittedListingId: null,
        listingDraft: null,
      };
    case CREATE_LISTING_DRAFT_SUCCESS:
      return {
        ...state,
        ...updateUploadedImagesState(state, payload),
        createListingDraftInProgress: false,
        submittedListingId: payload.data.id,
        listingDraft: payload.data,
      };
    case CREATE_LISTING_DRAFT_ERROR:
      return {
        ...state,
        createListingDraftInProgress: false,
        createListingDraftError: payload,
      };

    case PUBLISH_LISTING_REQUEST:
      return {
        ...state,
        publishingListing: payload.listingId,
        publishListingError: null,
      };
    case PUBLISH_LISTING_SUCCESS:
      return {
        ...state,
        redirectToListing: true,
        publishingListing: null,
        createListingDraftError: null,
        updateListingError: null,
        uploadImageError: null,
        createListingDraftInProgress: false,
        updateInProgress: false,
      };
    case PUBLISH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        publishingListing: null,
        publishListingError: {
          listingId: state.publishingListing,
          error: payload,
        },
      };
    }

    case UPDATE_LISTING_REQUEST:
      return { ...state, updateInProgress: true, updateListingError: null };
    case UPDATE_LISTING_SUCCESS:
      return { ...state, ...updateUploadedImagesState(state, payload), updateInProgress: false };
    case UPDATE_LISTING_ERROR:
      return { ...state, updateInProgress: false, updateListingError: payload };

    case SHOW_LISTINGS_REQUEST:
      return { ...state, showListingsError: null };
    case SHOW_LISTINGS_SUCCESS:
      return { ...state, images: {}, imageOrder: [], removedImageIds: [] };
    case SHOW_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, showListingsError: payload, redirectToListing: false };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);

      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case UPLOAD_VIDEO_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedVideos = {
        ...state.uploadedVideos,
        [payload.params.id]: {
          ...payload.params,
          uploading: true,
        },
      };
      return {
        ...state,
        uploadedVideos,
        uploadedVideosOrder: state.uploadedVideosOrder.concat([payload.params.id]),
        uploadVideoError: null,
      };
    }
    case UPLOAD_VIDEO_SUCCESS: {
      // payload.params: { id: 'tempId', videoId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedVideos = { ...state.uploadedVideos, [id]: { id, ...rest } };
      return { ...state, uploadedVideos };
    }
    case UPLOAD_VIDEO_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedVideosOrder = state.uploadedVideosOrder.filter(i => i !== id);
      const uploadedVideos = omit(state.uploadedVideos, id);
      return { ...state, uploadedVideosOrder, uploadedVideos, uploadVideoError: error };
    }

    case REMOVE_LISTING_VIDEO: {
      const id = payload.videoId;

      // Only mark the video removed if it hasn't been added to the
      // listing already
      const removedVideoIds = state.uploadedVideos[id]
        ? state.removedVideoIds
        : state.removedVideoIds.concat(id);

      // Always remove from the draft since it might be a new video to
      // an existing listing.
      const uploadedVideos = omit(state.uploadedVideos, id);
      const uploadedVideosOrder = state.uploadedVideosOrder.filter(i => i !== id);

      return { ...state, uploadedVideos, uploadedVideosOrder, removedVideoIds };
    }

    case FETCH_EXCEPTIONS_REQUEST:
      return {
        ...state,
        availabilityExceptions: [],
        fetchExceptionsError: null,
        fetchExceptionsInProgress: true,
      };
    case FETCH_EXCEPTIONS_SUCCESS:
      return {
        ...state,
        availabilityExceptions: payload,
        fetchExceptionsError: null,
        fetchExceptionsInProgress: false,
      };
    case FETCH_EXCEPTIONS_ERROR:
      return {
        ...state,
        fetchExceptionsError: payload.error,
        fetchExceptionsInProgress: false,
      };

    case ADD_EXCEPTION_REQUEST:
      return {
        ...state,
        addExceptionError: null,
        addExceptionInProgress: true,
      };
    case ADD_EXCEPTION_SUCCESS:
      return {
        ...state,
        availabilityExceptions: [...state.availabilityExceptions, payload],
        addExceptionInProgress: false,
      };
    case ADD_EXCEPTION_ERROR:
      return {
        ...state,
        addExceptionError: payload.error,
        addExceptionInProgress: false,
      };

    case DELETE_EXCEPTION_REQUEST:
      return {
        ...state,
        deleteExceptionId: payload.params.id,
        deleteExceptionError: null,
        deleteExceptionInProgress: true,
      };
    case DELETE_EXCEPTION_SUCCESS: {
      const deletedExceptionId = payload.id;
      const availabilityExceptions = state.availabilityExceptions.filter(
        e => e.id.uuid !== deletedExceptionId.uuid
      );
      return {
        ...state,
        deleteExceptionId: null,
        availabilityExceptions,
        deleteExceptionInProgress: false,
      };
    }
    case DELETE_EXCEPTION_ERROR:
      return {
        ...state,
        deleteExceptionId: null,
        deleteExceptionError: payload.error,
        deleteExceptionInProgress: false,
      };

    case ADD_REPEATING_EXCEPTIONS_REQUEST:
      return {
        ...state,
        addRepeatingExceptionsError: null,
        addRepeatingExceptionsInProgress: true,
      };
    case ADD_REPEATING_EXCEPTIONS_SUCCESS:
      return {
        ...state,
        availabilityExceptions: [...state.availabilityExceptions, ...payload],
        addRepeatingExceptionsInProgress: false,
      };
    case ADD_REPEATING_EXCEPTIONS_ERROR:
      return {
        ...state,
        addRepeatingExceptionsError: payload.error,
        addRepeatingExceptionsInProgress: false,
      };

    case DELETE_REPEATING_EXCEPTIONS_REQUEST:
      return {
        ...state,
        deleteRepeatingExceptionsError: null,
        deleteRepeatingExceptionsInProgress: true,
      };
    case DELETE_REPEATING_EXCEPTIONS_SUCCESS: {
      const deletedAvailabilityExceptionIds = payload.map(e => e.id.uuid);
      const filterAvailabilityExceptions = state.availabilityExceptions.filter(
        e => !deletedAvailabilityExceptionIds.includes(e.id.uuid)
      );
      return {
        ...state,
        availabilityExceptions: filterAvailabilityExceptions,
        deleteRepeatingExceptionsInProgress: false,
      };
    }
    case DELETE_REPEATING_EXCEPTIONS_ERROR:
      return {
        ...state,
        deleteRepeatingExceptionsError: payload.error,
        deleteRepeatingExceptionsInProgress: false,
      };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case PROMOTE_IMAGE_REQUEST:
      return { ...state, promoteImageInProgress: true, promoteImageError: null };
    case PROMOTE_IMAGE_SUCCESS:
      return {
        ...state,
        promoteImageInProgress: false,
        promoteImageError: null,
      };
    case PROMOTE_IMAGE_ERROR:
      return { ...state, promoteImageInProgress: false, promoteImageError: payload };

    case PROMOTE_VIDEO_REQUEST:
      return { ...state, promoteVideoInProgress: true, promoteVideoError: null };
    case PROMOTE_VIDEO_SUCCESS:
      return {
        ...state,
        promoteVideoInProgress: false,
        promoteVideoError: null,
      };
    case PROMOTE_VIDEO_ERROR:
      return { ...state, promoteVideoInProgress: false, promoteVideoError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const markTabUpdated = tab => ({
  type: MARK_TAB_UPDATED,
  payload: tab,
});

export const clearUpdatedTab = () => ({
  type: CLEAR_UPDATED_TAB,
});

export const clearUpdatedContent = () => ({
  type: CLEAR_UPDATED_CONTENT,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

export const removeListingVideo = videoId => ({
  type: REMOVE_LISTING_VIDEO,
  payload: { videoId },
});

// All the action creators that don't have the {Success, Error} suffix
// take the params object that the corresponding SDK endpoint method
// expects.

// SDK method: ownListings.create
export const createListingDraft = requestAction(CREATE_LISTING_DRAFT_REQUEST);
export const createListingDraftSuccess = successAction(CREATE_LISTING_DRAFT_SUCCESS);
export const createListingDraftError = errorAction(CREATE_LISTING_DRAFT_ERROR);

// SDK method: ownListings.publish
export const publishListing = requestAction(PUBLISH_LISTING_REQUEST);
export const publishListingSuccess = successAction(PUBLISH_LISTING_SUCCESS);
export const publishListingError = errorAction(PUBLISH_LISTING_ERROR);

// SDK method: ownListings.update
export const updateListing = requestAction(UPDATE_LISTING_REQUEST);
export const updateListingSuccess = successAction(UPDATE_LISTING_SUCCESS);
export const updateListingError = errorAction(UPDATE_LISTING_ERROR);

// SDK method: ownListings.show
export const showListings = requestAction(SHOW_LISTINGS_REQUEST);
export const showListingsSuccess = successAction(SHOW_LISTINGS_SUCCESS);
export const showListingsError = errorAction(SHOW_LISTINGS_ERROR);

// SDK method: images.upload
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);

// Google Storage API: googleStorageAPI.videos.upload
export const uploadVideoRequest = requestAction(UPLOAD_VIDEO_REQUEST);
export const uploadVideoSuccess = successAction(UPLOAD_VIDEO_SUCCESS);
export const uploadVideoError = errorAction(UPLOAD_VIDEO_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);

// SDK method: availabilityExceptions.create
export const addAvailabilityExceptionRequest = requestAction(ADD_EXCEPTION_REQUEST);
export const addAvailabilityExceptionSuccess = successAction(ADD_EXCEPTION_SUCCESS);
export const addAvailabilityExceptionError = errorAction(ADD_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

// flexIntegrationSdk method: availabilityExceptions.create (create multiple exceptions)
export const addRepeatingAvailabilityExceptionsRequest = requestAction(
  ADD_REPEATING_EXCEPTIONS_REQUEST
);
export const addRepeatingAvailabilityExceptionsSuccess = successAction(
  ADD_REPEATING_EXCEPTIONS_SUCCESS
);
export const addRepeatingAvailabilityExceptionsError = errorAction(ADD_REPEATING_EXCEPTIONS_ERROR);

// flexIntegrationSdk method: availabilityExceptions.delete (delete multiple exceptions)
export const deleteRepeatingAvailabilityExceptionsRequest = requestAction(
  DELETE_REPEATING_EXCEPTIONS_REQUEST
);
export const deleteRepeatingAvailabilityExceptionsSuccess = successAction(
  DELETE_REPEATING_EXCEPTIONS_SUCCESS
);
export const deleteRepeatingAvailabilityExceptionsError = errorAction(
  DELETE_REPEATING_EXCEPTIONS_ERROR
);

export const savePayoutDetailsRequest = requestAction(SAVE_PAYOUT_DETAILS_REQUEST);
export const savePayoutDetailsSuccess = successAction(SAVE_PAYOUT_DETAILS_SUCCESS);
export const savePayoutDetailsError = errorAction(SAVE_PAYOUT_DETAILS_ERROR);

export const promoteImageRequest = requestAction(PROMOTE_IMAGE_REQUEST);
export const promoteImageSuccess = successAction(PROMOTE_IMAGE_SUCCESS);
export const promoteImageError = errorAction(PROMOTE_IMAGE_ERROR);

export const promoteVideoRequest = requestAction(PROMOTE_VIDEO_REQUEST);
export const promoteVideoSuccess = successAction(PROMOTE_VIDEO_SUCCESS);
export const promoteVideoError = errorAction(PROMOTE_VIDEO_ERROR);

// ================ Thunk ================ //

export function requestShowListing(actionPayload) {
  return async (dispatch, getState, sdk) => {
    dispatch(showListings(actionPayload));

    // Live Room listings are always managed by the creator (owner),
    // so always use sdk.ownListings.show() directly.
    return sdk.ownListings
      .show(actionPayload)
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(showListingsSuccess(response));
        return response;
      })
      .catch(e => dispatch(showListingsError(storableError(e))));
  };
}

export function requestCreateListingDraft(data) {
  return async (dispatch, getState, sdk) => {
    dispatch(createListingDraft(data));

    return sdk.ownListings
      .createDraft(data, editLiveRoomIncludeParams())
      .then(response => {
        //const id = response.data.data.id.uuid;

        // Add the created listing to the marketplace data
        dispatch(addMarketplaceEntities(response));

        // Modify store to understand that we have created listing and can redirect away
        dispatch(createListingDraftSuccess(response));
        return response;
      })
      .catch(e => {
        log.error(e, 'create-listing-draft-failed', { listingData: data });
        return dispatch(createListingDraftError(storableError(e)));
      });
  };
}

export const requestPublishListingDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(publishListing(listingId));

  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(response => {
      // Add the created listing to the marketplace data
      dispatch(addMarketplaceEntities(response));
      dispatch(publishListingSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(publishListingError(storableError(e)));
    });
};

export function requestImageUpload(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;

    dispatch(uploadImageRequest(actionPayload));
    // Resized in the browser first: uploads are billed by size.
    return prepareImageForUpload(actionPayload.file)
      .then(file => sdk.images.upload({ image: file }, editLiveRoomImageUploadParams()))
      .then(resp => {
        const img = resp.data.data;
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        return dispatch(
          uploadImageSuccess({ data: { ...img, id, imageId: img.id, file: actionPayload.file } })
        );
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export function requestVideoUpload(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;

    dispatch(uploadVideoRequest(actionPayload));

    const formData = new FormData();
    formData.append('video', actionPayload.file);

    return googleStorageAPI.videos
      .upload(formData)
      .then(resp => {
        const video = resp;
        // Uploaded video has an existing id that refers to file
        return dispatch(
          uploadVideoSuccess({
            data: { ...video, id, videoId: video.id, file: actionPayload.file },
          })
        );
      })
      .catch(e => dispatch(uploadVideoError({ id, error: storableError(e) })));
  };
}

// Update the given tab of the wizard with the given data. This saves
// the data to the listing, and marks the tab updated so the UI can
// display the state.
export function requestUpdateListing(tab, data, isOwnListing) {
  return async (dispatch, getState, sdk) => {
    dispatch(updateListing(data));

    // Only an author's save carries images: for anyone else the server's route refuses them
    const { images, ...rest } = data;
    const imageProperty = isOwnListing && images ? { images: imageIds(images) } : {};

    const updateValues = { ...rest, ...imageProperty };

    const api = isOwnListing ? sdk.ownListings : listingsAPI;

    try {
      const updateResponse = await api.update(updateValues, editLiveRoomIncludeParams());

      dispatch(markTabUpdated(tab));
      dispatch(addMarketplaceEntities(updateResponse));
      dispatch(updateListingSuccess(updateResponse));

      return updateResponse;
    } catch (e) {
      log.error(e, 'update-listing-failed', { listingData: data });
      dispatch(updateListingError(storableError(e)));
      throw e;
    }
  };
}

export const requestAddAvailabilityException = (params, isOwnListing) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(addAvailabilityExceptionRequest(params));

  const useSdk = isOwnListing ? sdk.availabilityExceptions : availabilityExceptionsAPI;

  return useSdk
    .create(params, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      dispatch(addAvailabilityExceptionSuccess({ data: availabilityException }));

      return availabilityException;
    })
    .catch(e => {
      dispatch(addAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

/**
 * Move an episode to a new time (server/api/episodes/reschedule.js), then
 * reload the listing and its time slots so the Schedules tab shows it moved.
 * Resolves to the server's answer, or rejects with its error.
 */
export const rescheduleEpisode = ({ listing, episodeId, start, end }) => async dispatch => {
  const response = await episodeRsvpAPI.reschedule(listing.id.uuid, episodeId, {
    start: start.valueOf(),
    end: end.valueOf(),
  });
  await dispatch(
    requestShowListing({
      id: listing.id,
      include: ['author', 'images'],
      ...imageVariantsForSets('editLiveRoom'),
    })
  );
  const tz = listing.attributes?.availabilityPlan?.timezone || getDefaultTimeZoneOnBrowser();
  const today = new Date();
  await dispatch(
    requestFetchAvailabilityExceptions({
      listingId: listing.id,
      start: resetToStartOfDay(today, tz, 0),
      end: resetToStartOfDay(today, tz, 364),
    })
  );
  return response?.data ?? response;
};

/**
 * A Free creator's attendee-hours this month, for the Schedules tab. Pro
 * creators and organizations' Live Rooms come back as not limited.
 */
export const fetchAttendeeUsage = () => async dispatch => {
  try {
    const response = await attendeeUsageAPI.mine();
    const body = response?.data ?? response;
    dispatch({ type: SET_ATTENDEE_USAGE, payload: body?.data ?? null });
  } catch (e) {
    dispatch({ type: SET_ATTENDEE_USAGE, payload: null });
  }
};

export const requestDeleteAvailabilityException = (params, listingId, isOwnListing) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(deleteAvailabilityExceptionRequest(params));

  const useSdk = isOwnListing ? sdk.availabilityExceptions : availabilityExceptionsAPI;
  const requestParams = isOwnListing ? params : { ...params, listingId };

  return useSdk
    .delete(requestParams, { expand: true })
    .then(response => {
      const availabilityException = response.data.data;
      return dispatch(deleteAvailabilityExceptionSuccess({ data: availabilityException }));
    })
    .catch(e => {
      dispatch(deleteAvailabilityExceptionError({ error: storableError(e) }));
      throw e;
    });
};

export const requestFetchAvailabilityExceptions = (fetchParams, isOwnListing) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchAvailabilityExceptionsRequest(fetchParams));

  const integrationResponse = await listingsAPI.show({
    id: fetchParams.listingId.uuid,
    include: ['author'],
  });
  const currentListing = denormalisedResponseEntities(integrationResponse.data)[0];

  const { currentUser } = getState().user;
  const isOwn = isOwnListing || currentUser?.id?.uuid === currentListing?.author?.id?.uuid;

  const useSdk = isOwn ? sdk.availabilityExceptions : availabilityExceptionsAPI;
  const params = isOwn ? fetchParams : { ...fetchParams, listingId: fetchParams.listingId.uuid };

  return useSdk
    .query(params, { expand: true })
    .then(fetchResponse => {
      const response = isOwn ? fetchResponse : fetchResponse.data;
      const availabilityExceptions = denormalisedResponseEntities(response);
      return dispatch(fetchAvailabilityExceptionsSuccess({ data: availabilityExceptions }));
    })
    .catch(e => {
      return dispatch(fetchAvailabilityExceptionsError({ error: storableError(e) }));
    });
};

export const requestAddRepeatingAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  dispatch(addRepeatingAvailabilityExceptionsRequest(params));

  return availabilityExceptionsAPI
    .createMultiple(params)
    .then(response => {
      const availabilityExceptions = response.data.data;
      const denormalisedAvailabilityExceptions = denormaliseIntegrationAvailabilityExceptions(
        availabilityExceptions
      );

      dispatch(
        addRepeatingAvailabilityExceptionsSuccess({ data: denormalisedAvailabilityExceptions })
      );
      return availabilityExceptions;
    })
    .catch(e => {
      dispatch(addRepeatingAvailabilityExceptionsError({ error: storableError(e) }));
      throw e;
    });
};

export const requestDeleteRepeatingAvailabilityExceptions = params => (dispatch, getState, sdk) => {
  dispatch(deleteRepeatingAvailabilityExceptionsRequest(params));

  return availabilityExceptionsAPI
    .deleteMultiple(params)
    .then(response => {
      const availabilityExceptions = response.data.data;
      return dispatch(
        deleteRepeatingAvailabilityExceptionsSuccess({ data: availabilityExceptions })
      );
    })
    .catch(e => {
      dispatch(deleteRepeatingAvailabilityExceptionsError({ error: storableError(e) }));
      throw e;
    });
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const promoteImage = (listingId, imageId, imageType, isOwnListing) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(promoteImageRequest());

    const publicDataProperty = getPromotePublicDataProperty(imageType);
    const listingSdk = getListingSdk(isOwnListing, sdk);

    // When setting a cover image, unset cover video (mutual exclusivity)
    const publicData = {
      [publicDataProperty]: imageId ? imageId.uuid : null,
    };

    if (imageType === 'cover') {
      publicData.coverVideoId = null; // Remove cover video when setting cover image
    }

    const updateResponse = await listingSdk.update(
      {
        id: listingId,
        publicData,
      },
      { expand: true }
    );

    dispatch(addMarketplaceEntities(updateResponse));
    dispatch(promoteImageSuccess(updateResponse));

    return updateResponse;
  } catch (error) {
    dispatch(promoteImageError(storableError(error)));
  }
};

export const promoteVideo = (listingId, videoId, videoType, isOwnListing) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(promoteVideoRequest());

    const publicDataProperty = getPromoteVideoPublicDataProperty(videoType);
    const listingSdk = getListingSdk(isOwnListing, sdk);

    // When setting a cover video, unset cover image (mutual exclusivity)
    const publicData = {
      [publicDataProperty]: videoId || null,
    };

    if (videoType === 'cover') {
      publicData.coverImageId = null; // Remove cover image when setting cover video
    }

    const updateResponse = await listingSdk.update(
      {
        id: listingId,
        publicData,
      },
      { expand: true }
    );

    dispatch(addMarketplaceEntities(updateResponse));
    dispatch(promoteVideoSuccess(updateResponse));

    return updateResponse;
  } catch (error) {
    dispatch(promoteVideoError(storableError(error)));
  }
};

// loadData is run for each tab of the wizard. When editing an
// existing listing, the listing must be fetched first.
export const loadData = params => async (dispatch, getState, sdk) => {
  dispatch(clearUpdatedTab());
  dispatch(clearUpdatedContent());

  return dispatch(fetchOrganizationProfiles()).then(async organizationProfiles => {
    const { id, type } = params;

    if (type === 'new') {
      // No need to listing data when creating a new listing
      try {
        const response = await Promise.all([dispatch(fetchCurrentUser())]);
        const currentUser = getState().user.currentUser;

        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }

        return response;
      } catch (e) {
        throw e;
      }
    }

    const payload = {
      id: new UUID(id),
      include: ['author', 'images'],
      ...imageVariantsForSets('editLiveRoom'),
    };

    try {
      const response = await Promise.all([
        dispatch(requestShowListing(payload)),
        dispatch(fetchCurrentUser()),
      ]);
      const currentUser = getState().user.currentUser;

      if (currentUser && currentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }

      if (params.tab === 'schedules') {
        dispatch(fetchAttendeeUsage());
      }

      // Returning from Stripe-hosted onboarding — refresh the cached
      // payoutsEnabled flag on this seller's publicData so buyers'
      // subsequent LiveRoomPage loads see the updated state without
      // each one hitting Stripe.
      if (params.returnURLType === 'success' && currentUser?.id?.uuid) {
        dispatch(payoutsEnabled(currentUser.id.uuid, { refresh: true })).catch(() => null);
      }

      // Because of two dispatch functions, response is an array.
      // We are only interested in the response from requestShowListing here,
      // so we need to pick the first one
      if (response[0].data && response[0].data.data) {
        const listing = response[0].data.data?.id
          ? response[0].data.data
          : response[0].data.data.data;

        // If the listing doesn't have availabilityPlan yet
        // use the defaul timezone
        const availabilityPlan = listing?.attributes?.availabilityPlan;

        const tz = availabilityPlan
          ? listing.attributes.availabilityPlan.timezone
          : typeof window !== 'undefined'
          ? getDefaultTimeZoneOnBrowser()
          : 'Etc/UTC';

        const today = new Date();
        const start = resetToStartOfDay(today, tz, 0);
        // Query range: today + 364 days
        const exceptionRange = 364;
        const end = resetToStartOfDay(today, tz, exceptionRange);

        // NOTE: in this template, we don't expect more than 100 exceptions.
        // If there are more exceptions, pagination kicks in and we can't use frontend sorting.
        const params = {
          listingId: listing.id,
          start,
          end,
        };
        dispatch(requestFetchAvailabilityExceptions(params));
      }

      return response;
    } catch (e) {
      throw e;
    }
  });
};
